// ---------------------------------------------------------------------------
// spintaxLlm.js
// Two-call LLM strategy:
//   Call 1 — generate ONE keyword-specific content angle (short)
//   Call 2 — generate a 2000-word spintax article using that angle
//
// Title tags are generated via the 3-phase specialist prompt
// (titlePrompt.js) and injected into the spintax template.
// ---------------------------------------------------------------------------
import {stripGeo} from './geoSanitiser.js';
import {callAnthropicMessage,callAnthropicMessageWithMeta} from './anthropicClient.js';
import {parseLlmJson} from './jsonRepair.js';
import {DESCRIPTION_SYSTEM,buildDescriptionPrompt,sanitiseDescription,hasDescriptionCore} from './descriptionPrompt.js';
import {TITLE_SYSTEM_PROMPT,buildTitleGenerationPrompt,parseTitlesFromResponse,} from './titlePrompt.js';

const ANGLE_SYSTEM=`You are an expert SEO strategist. Return ONLY valid JSON. No markdown,no code fences,no explanation.`;

const SPINTAX_SYSTEM=`You are an experienced product reviewer and editor writing one complete, well-structured review article for a real person who is deciding whether to buy. Return ONLY valid JSON. No markdown,no code fences,no explanation.

WRITE LIKE A REAL WRITER (most important):
- It is ONE article with a logical flow, not a set of separate answers. Each section builds on the previous one, and paragraphs connect with natural transitions.
- Never repeat yourself. Every fact, figure, example and opinion appears in exactly ONE place. The intro previews; later sections add new detail and never restate earlier sentences.
- Paragraphs of 2-5 sentences. Mix short and long sentences. Use concrete specifics, not summaries of summaries.
- No research commentary: never write "the text I could access", "I did not verify", "I found no", "according to my research", "the fact sheet", "sources say". Just write the article.
- Editorial "we" is fine for opinions ("we think it suits agencies best"), never for testing.

WRITING STYLE:
- Write like a knowledgeable friend explaining things, not like marketing copy. Short, clear sentences. Everyday words.
- Lead with the answer. The first sentence of each section should directly answer the question the section is about.
- Be useful: explain what a feature actually means for the reader, who it suits, who it doesn't, the trade-offs, and what to check before buying.
- Include honest limitations and downsides alongside the strengths. A balanced review is more trustworthy.
- Speak to the reader as "you". Vary sentence length and openings so it doesn't sound formulaic.
- Avoid hype and filler: no "game-changer", "revolutionary", "unlock", "skyrocket", "in today's digital landscape", "look no further", "it's important to note".
- NO sales calls-to-action anywhere: never write "Try It Today", "Order Now", "Buy Now", "Act Now", "Where to Buy", "Claim Your Discount", "Limited Time Offer", "Click Here" or similar. Inform, don't push.

HONESTY RULES:
- Do NOT claim first-hand use or testing ("I tried", "we tested", "my results") and do NOT invent statistics, prices, testimonials, ratings, or results.
- The official website is the source of truth. State its features, pricing, terms and figures as facts (no "the vendor claims", "allegedly" or "not verified" hedging), and add your own practical analysis of what they mean for the reader.
- If a detail isn't in the source context, don't make it up. Give the reader a question to check instead (e.g. "check the current price and refund terms before you buy").

KEYWORD USE:
- The product name must be written exactly as given,with spaces preserved. Use it naturally, the way a person would.
- The full search keyword phrase is long and awkward in sentences. Use it at most ONCE per field, and only where it reads naturally; otherwise just use the product name.

FORMAT RULES:
- NEVER include country names,country codes,or geographic regions.
- Banned: UK,US,USA,CA,AU,AUS,NZ,IE,ZA,IN,GB,United Kingdom,United States,Canada,Australia,New Zealand,Ireland,South Africa,India.
- Spintax is LIGHT: use the {option1|option2|option3} format on short phrases only (2-3 options), roughly one group every one or two sentences. Never spin whole sentences or paragraphs.
- Spintax groups must be semantically equivalent — all options must make grammatical sense in context and keep the same natural tone.
- NEVER put a {inside another {without a matching} first (no nested braces).
- Content must be specific to the product — no generic filler.`;

const buildAnglePrompt=(keyword,product,pageText)=> {
  const ctx=pageText ? `\nSource page context (use for factual grounding):\n---\n${pageText.slice(0,2000)}\n---\n` : '';
  return `Generate ONE highly specific SEO content angle for a review page.
Keyword: "${keyword}"
Product: "${product}"
${ctx}
The angle must:
- Be specific to "${keyword}" and "${product}"
- Reflect a real buyer search intent, framed around the question a buyer is trying to answer (e.g. whether it's worth the money, who it's for, what the catch is)
- Be 8-20 words
- Have NO geo terms

Return ONLY this JSON: {"angle": "your single angle string here"}`.trim();
};

const buildSpintaxPrompt=(keyword,product,angle,pageText,titleSpintax,concise=false,research=null)=> {
  // Facts come from the research layer when available (official site +
  // real user reviews found by web search); otherwise from the crawled page.
  const ctx=research?.factSheet
    ? `\nRESEARCH FACT SHEET (your ONLY source of facts; built from the official website and real user reviews found by web search):\n---\n${research.factSheet}\n---\n`
    : pageText ? `\nOfficial website content (source of truth; state it as fact):\n---\n${pageText.slice(0,2500)}\n---\n` : '';
  const evidence=research?.evidence ? `\n${research.evidence}\n` : '';
  // Used on retry after a cut-off answer: same structure, shorter text.
  const length=concise
    ? 'Target about 1,200 words for the article: 1 paragraph per section, 3 features, 3 FAQs.'
    : 'Target about 1,800-2,200 words for the article.';
  return `Write a complete review article about "${product}" as JSON. ${length}
Search keyword: "${keyword}"
Angle: "${angle}"
${ctx}${evidence}
HOW TO USE THE FACTS:
- The official website is the source of truth: state its features, pricing, terms and figures as facts.
- Turn facts into analysis: what a feature means in practice, who benefits, its limits, how it compares with the alternatives.
- User opinions come ONLY from the fact sheet's user_reviews. Weave them into prose, naming the kind of source ("a BlackHatWorld user who had run it for a month", "Trustpilot reviewers"). No URLs in the text, no invented quotes. If the fact sheet has no user reviews, return an empty paragraphs array for user_feedback.
- Only describe hands-on testing if OWNER-SUPPLIED EVIDENCE exists.

ARTICLE OUTLINE (fill every part; the order is the order a reader meets it):
- standfirst: 1-2 sentences (max 35 words) shown under the headline. A crisp summary of the verdict, worded differently from the intro.
- article.intro: 2 paragraphs. Paragraph 1 opens with a direct, qualified answer to whether ${product} is worth it and for whom. Paragraph 2 says what the product is, its biggest strength and biggest limitation in one line each, and what the article covers. Never open with filler ("In this walkthrough", "Are you wondering", "Let's dive in").
- what_it_is: 1-2 paragraphs: what it is, who makes it, what problem it solves.
- how_it_works: 1-2 paragraphs plus 3-6 short steps describing the actual workflow.
- features: a 1-sentence lead-in, then 3-4 items, each with its own subheading and 1-2 paragraphs of analysis (what it does, why it matters, its limits).
- user_feedback: 2-3 paragraphs telling the story of what real users report: praise first, then complaints, with the kind of source named in the sentence.
- pricing: 1-2 paragraphs on cost and value, including costs outside the licence if the facts mention them.
- pros_cons: 4-5 pros and 3-4 cons, each a short, specific phrase or sentence.
- who_for: 1 paragraph, plus 3 "good fit" and 3 "poor fit" bullets, each with the reason.
- alternatives: 1-2 paragraphs comparing it with the realistic alternatives (named competitors from the facts, or doing it manually).
- before_you_buy: 2-4 short things to confirm that are genuinely not published (e.g. trial length, refund terms). Never doubts about what the official site states.
- verdict: 2 paragraphs with a clear opinion that follows from everything above: who it makes sense for and why, and who gets weaker value.
- faq: 4-5 questions buyers ask, each answered directly in 2-3 sentences, without repeating sentences from the article.
- Every heading: natural and reader-friendly, spun as {variant A|variant B|variant C}. Use the product name, never the long search keyword.
- evidence_commentary: 2-3 sentences referencing the OWNER-SUPPLIED EVIDENCE directly, or "" if none.

VIDEO CARDS (shown beside the article as "More videos"):
- card_titles: EXACTLY 9 short questions a buyer asks (e.g. "Is ${product} Worth It for Agencies?").
- card_contents: EXACTLY 9 teasers of 45-70 words, card_contents[i] answering card_titles[i]. Write them fresh: do NOT copy or closely paraphrase any sentence from the article, and do not restate the verdict. Topics in order: [0] who it's worth it for, [1] what it does, [2] the standout feature with a practical scenario, [3] the main drawbacks, [4] what it really costs, [5] who should skip it, [6] getting started, [7] a common buyer concern, [8] the key question to ask before buying.

PAGE METADATA (short):
- subtitle: a short parenthetical like "(The Honest ${product} Breakdown)"; trailing: a short phrase about its main features.
- meta_description: ~155 chars answering what ${product} is and who it's for. og_description: 2 sentences. twitter_description: 1 sentence. json_ld_description: 2 sentences. hyd_og_description / hyd_twitter_description: reworded versions.
- secondary_video_subtitles: 4 short parenthetical subtitles; secondary_chapter_sets: 4 arrays of 4 short chapter names.

SPINTAX: light, on short phrases only, never inside the product name or the search keyword, no nested braces.

Return ONLY this JSON object, with "article" first:
{"standfirst":"...","article":{"intro":["...","..."],"what_it_is":{"heading":"...","paragraphs":["..."]},"how_it_works":{"heading":"...","paragraphs":["..."],"steps":["...","..."]},"features":{"heading":"...","lead":"...","items":[{"heading":"...","paragraphs":["..."]}]},"user_feedback":{"heading":"...","paragraphs":["...","..."]},"pricing":{"heading":"...","paragraphs":["..."]},"pros_cons":{"heading":"...","pros":["..."],"cons":["..."]},"who_for":{"heading":"...","paragraphs":["..."],"good_fit":["..."],"poor_fit":["..."]},"alternatives":{"heading":"...","paragraphs":["..."]},"before_you_buy":{"heading":"...","items":["..."]},"verdict":{"heading":"...","paragraphs":["...","..."]},"faq":{"heading":"...","items":[{"q":"...","a":"..."}]}},"evidence_commentary":"","card_titles":["...","...","...","...","...","...","...","...","..."],"card_contents":["...","...","...","...","...","...","...","...","..."],"subtitle":"...","trailing":"...","meta_description":"...","og_description":"...","twitter_description":"...","json_ld_description":"...","hyd_og_description":"...","hyd_twitter_description":"...","secondary_video_subtitles":["...","...","...","..."],"secondary_chapter_sets":[["...","...","...","..."],["...","...","...","..."],["...","...","...","..."],["...","...","...","..."]]}

CRITICAL: Return ONLY valid JSON. No geo terms. One coherent article; no repeated facts or sentences anywhere on the page.`.trim();
};

const parseJson=(text)=> parseLlmJson(text).data;

// Number of usable card topics: 6 to 9, limited by whichever of the
// titles/contents arrays is shorter so titles and descriptions stay paired.
const cardPoolSize=(raw)=> {
  const titles=Array.isArray(raw.card_titles) ? raw.card_titles.length : 0;
  const contents=Array.isArray(raw.card_contents) ? raw.card_contents.length : 0;
  return Math.max(6,Math.min(9,titles,contents));
};

const sanitiseSpintaxContent=(raw)=> {
  const str=(v)=> (typeof v==='string' ? stripGeo(v.trim()) : '');
  // Like arr() but never pads with copies: missing items are just left out.
  const list=(v,max)=> (Array.isArray(v) ? v : []).map((x)=> str(x)).filter(Boolean).slice(0,max);
  const arr=(v,len)=> {
    const a=Array.isArray(v) ? v : [];
    while (a.length < len) a.push(a[a.length - 1] || '');
    return a.slice(0,len).map((x)=> str(x));
  };
  const a=raw.article && typeof raw.article==='object' ? raw.article : {};
  const section=(v,extra={})=> {
    const o=v && typeof v==='object' ? v : {};
    const out={heading: str(o.heading),paragraphs: list(o.paragraphs,4)};
    for (const [key,max] of Object.entries(extra)) out[key]=list(o[key],max);
    return out;
  };
  const article={
    intro: list(a.intro,3),
    what_it_is: section(a.what_it_is),
    how_it_works: section(a.how_it_works,{steps: 6}),
    features: {
      heading: str(a.features?.heading),
      lead: str(a.features?.lead),
      items: (Array.isArray(a.features?.items) ? a.features.items : [])
        .map((item)=> ({heading: str(item?.heading),paragraphs: list(item?.paragraphs,3)}))
        .filter((item)=> item.heading && item.paragraphs.length)
        .slice(0,4),
    },
    user_feedback: section(a.user_feedback),
    pricing: section(a.pricing),
    pros_cons: {heading: str(a.pros_cons?.heading),pros: list(a.pros_cons?.pros,5),cons: list(a.pros_cons?.cons,4)},
    who_for: section(a.who_for,{good_fit: 3,poor_fit: 3}),
    alternatives: section(a.alternatives),
    before_you_buy: {heading: str(a.before_you_buy?.heading),items: list(a.before_you_buy?.items,4)},
    verdict: section(a.verdict),
    faq: {
      heading: str(a.faq?.heading),
      items: (Array.isArray(a.faq?.items) ? a.faq.items : [])
        .map((item)=> ({q: str(item?.q),a: str(item?.a)}))
        .filter((item)=> item.q && item.a)
        .slice(0,5),
    },
  };
  return {
    standfirst: str(raw.standfirst),
    article,
    // "YouTube video description" content style (null in article style).
    description: raw.description && typeof raw.description==='object' ? sanitiseDescription(raw.description,str,list) : null,
    subtitle: str(raw.subtitle) || '(The Honest Breakdown)',
    trailing: str(raw.trailing) || 'Features, Pricing, Official Site',
    meta_description: str(raw.meta_description),
    og_description: str(raw.og_description),
    twitter_description: str(raw.twitter_description),
    breadcrumb_blurb: str(raw.breadcrumb_blurb),
    main_video_description: str(raw.main_video_description),
    json_ld_description: str(raw.json_ld_description),
    // Up to 9 card topics; each page shows 6 of them (spintaxPageDeriver).
    card_titles: arr(raw.card_titles,cardPoolSize(raw)),
    card_contents: arr(raw.card_contents,cardPoolSize(raw)),
    video_chapters: arr(raw.video_chapters,8),
    hyd_subtitle_a: str(raw.hyd_subtitle_a) || str(raw.subtitle),
    hyd_subtitle_b: str(raw.hyd_subtitle_b) || str(raw.subtitle),
    hyd_subtitle_c: str(raw.hyd_subtitle_c) || str(raw.subtitle),
    hyd_subtitle_d: str(raw.hyd_subtitle_d) || str(raw.subtitle),
    hyd_og_description: str(raw.hyd_og_description) || str(raw.og_description),
    hyd_twitter_description: str(raw.hyd_twitter_description) || str(raw.twitter_description),
    secondary_video_subtitles: arr(raw.secondary_video_subtitles,4),
    secondary_chapter_sets: Array.isArray(raw.secondary_chapter_sets) ? raw.secondary_chapter_sets.slice(0,4).map((s)=> arr(s,4)) : [[],[],[],[]],
    seo_title_spintax: str(raw.seo_title_spintax) || '',
    // Evaluation article (optional: rendered only when present).
    evaluation_verdict: str(raw.evaluation_verdict),
    evaluation_criteria: list(raw.evaluation_criteria,5),
    pros: list(raw.pros,5),
    cons: list(raw.cons,4),
    best_for: list(raw.best_for,3),
    not_for: list(raw.not_for,3),
    faq: (Array.isArray(raw.faq) ? raw.faq : [])
      .map((item)=> ({q: str(item?.q),a: str(item?.a)}))
      .filter((item)=> item.q && item.a)
      .slice(0,5),
    what_it_does: str(raw.what_it_does),
    workflow: list(raw.workflow,6),
    feature_analysis: (Array.isArray(raw.feature_analysis) ? raw.feature_analysis : [])
      .map((item)=> ({feature: str(item?.feature),analysis: str(item?.analysis)}))
      .filter((item)=> item.feature && item.analysis)
      .slice(0,4),
    alternatives_comparison: str(raw.alternatives_comparison),
    pricing_analysis: str(raw.pricing_analysis),
    user_feedback: str(raw.user_feedback),
    evidence_commentary: str(raw.evidence_commentary),
    final_verdict: str(raw.final_verdict),
  };
};

export const generateAngle=async (keyword,product,apiKey,model,pageText,onRetry=null,proxyUrl=null)=> {
  try {
    const raw=await callAnthropicMessage(
      apiKey,model,ANGLE_SYSTEM,buildAnglePrompt(keyword,product,pageText),
      300,3,onRetry,proxyUrl
    );
    const parsed=parseJson(raw);
    const angle=stripGeo(String(parsed.angle || '').trim());
    if (!angle) throw new Error('angle field empty');
    return angle;
  } catch (err) {
    console.warn(`[angle] Failed (${err.message}),using fallback`);
    return `honest ${product} review — what buyers need to know before purchasing`;
  }
};

export const generateSpecialistTitles=async (
  keyword,apiKey,model,titleCount=10,onRetry=null,proxyUrl=null
)=> {
  const batchSize=Math.min(titleCount,20);
  try {
    const raw=await callAnthropicMessage(
      apiKey,model,TITLE_SYSTEM_PROMPT,buildTitleGenerationPrompt(keyword,batchSize),
      3000,2,onRetry,proxyUrl
    );
    const titles=parseTitlesFromResponse(raw,keyword,batchSize);
    const result=[];
    for (let i=0;i < titleCount;i++) {
      result.push(titles[i % titles.length] || keyword.slice(0,60));
    }
    return result;
  } catch (err) {
    console.warn(`[specialist_titles] Failed (${err.message}),using keyword fallback`);
    return Array.from({length: titleCount},()=> keyword.slice(0,60));
  }
};

// A spintax article with 3+ options per phrase often needs more than 8000
// output tokens; at 8000 the JSON was cut off and could not be parsed.
const SPINTAX_MAX_TOKENS=16000;

// "max_tokens: 16000 > 8192, which is the maximum allowed ..." -> 8192
const maxAllowedTokens=(message)=> {
  const match=String(message || '').match(/max_tokens:\s*\d+\s*>\s*(\d+)/i);
  return match ? Number(match[1]) : null;
};

// The article and the cards are present and complete (not cut off).
const ARTICLE_SECTIONS=['what_it_is','how_it_works','pricing','who_for','alternatives','verdict'];
const hasArticleCore=(data)=> {
  const a=data?.article;
  if (!a || !Array.isArray(a.intro) || a.intro.length < 2) return false;
  const filled=ARTICLE_SECTIONS.filter((k)=> Array.isArray(a[k]?.paragraphs) && a[k].paragraphs.some((p)=> typeof p==='string' && p.trim()));
  return filled.length >=5
    && Array.isArray(a.features?.items) && a.features.items.length >=2
    && Array.isArray(data.card_titles) && data.card_titles.length >=6
    && Array.isArray(data.card_contents) && data.card_contents.length >=6
    && data.card_contents.slice(0,6).every((text)=> typeof text==='string' && text.trim());
};

export const generateSpintaxContent=async (
  keyword,product,angle,apiKey,model,pageText,onRetry=null,titleSpintax=null,proxyUrl=null,log=null,research=null
)=> {
  let maxTokens=SPINTAX_MAX_TOKENS;
  // Content style: a short YouTube-style video description, or the full article.
  const description=research?.style==='description';
  const SYSTEM=description ? DESCRIPTION_SYSTEM : SPINTAX_SYSTEM;
  const buildPrompt=(concise)=> (description
    ? buildDescriptionPrompt(keyword,product,angle,pageText,concise,research)
    : buildSpintaxPrompt(keyword,product,angle,pageText,titleSpintax,concise,research));
  const hasCoreFields=description ? hasDescriptionCore : hasArticleCore;
  const what=description ? 'description' : 'article';

  const request=async (prompt)=> {
    try {
      return await callAnthropicMessageWithMeta(apiKey,model,SYSTEM,prompt,maxTokens,3,onRetry,proxyUrl);
    } catch (err) {
      // Older models allow fewer output tokens; retry at their maximum.
      const allowed=maxAllowedTokens(err.message);
      if (!allowed || allowed >=maxTokens) throw err;
      log?.(`⚠ Model allows at most ${allowed} output tokens; using that limit.`);
      maxTokens=allowed;
      return callAnthropicMessageWithMeta(apiKey,model,SYSTEM,prompt,maxTokens,3,onRetry,proxyUrl);
    }
  };

  const attempt=async (concise)=> {
    const result=await request(buildPrompt(concise));
    const truncated=result.stopReason==='max_tokens';
    try {
      const {data,repaired}=parseLlmJson(result.text);
      return {data,repaired,truncated,error: null};
    } catch (error) {
      return {data: null,repaired: false,truncated,error};
    }
  };

  const first=await attempt(false);
  if (hasCoreFields(first.data)) {
    if (first.truncated || first.repaired) log?.(`⚠ Claude's ${what} JSON was cut off or malformed; repaired it automatically.`);
    return sanitiseSpintaxContent(first.data);
  }

  log?.(`⚠ Claude's ${what} ${first.truncated ? `was cut off at the ${maxTokens}-token limit` : 'was incomplete or not valid JSON'}; retrying with a shorter version...`);
  const second=await attempt(true);
  if (hasCoreFields(second.data)) {
    if (second.truncated || second.repaired) log?.('⚠ Retry output was repaired automatically.');
    return sanitiseSpintaxContent(second.data);
  }

  // Use whatever was recovered rather than failing the whole run.
  const partial=second.data || first.data;
  if (partial && typeof partial==='object') {
    log?.('⚠ Using partially recovered article content; some sections may repeat.');
    return sanitiseSpintaxContent(partial);
  }

  throw new Error(
    `Claude's article could not be parsed as JSON after a retry` +
    `${second.truncated ? ` (output was cut off at ${maxTokens} tokens; try a model with a larger output limit, e.g. a Sonnet model)` : ''}.`
  );
};